import React from 'react'
import PropTypes from 'prop-types'
import { Map, OrderedMap } from 'immutable'
import Editor from '@draft-js-plugins/editor'
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar'
import createSingleLinePlugin from 'draft-js-single-line-plugin'
import createToolbarPlugin from '@draft-js-plugins/static-toolbar'
import {
  getActiveRichTextContentElementIndex,
  getNextRichTextContentElement
} from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/rich-text'
import createMultilineLimitPlugin from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/multiline-limit-plugin'
import createArrowNavigationPlugin from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/arrow-navigation-plugin'
import {
  EditorState,
  ContentState,
  RichUtils,
  Modifier,
  getDefaultKeyBinding,
  KeyBindingUtil,
  convertFromHTML
} from 'draft-js'
import {
  BoldButton,
  ItalicButton,
  UnderlineButton,
  HeadlineOneButton,
  HeadlineTwoButton,
  HeadlineThreeButton,
  BlockquoteButton
} from '@draft-js-plugins/buttons'
import convertBlockMapToHTML from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/convert-block-map-to-html'
import { setSelectionAfterUpdate } from '@aytm/lib/rfd-ui/components/advanced/lib/draft-js/selection-tools'
import { listPipe, escapeHTML } from '@aytm/lib/common/func'
import { KEYCODE_UP, KEYCODE_DOWN, KEYCODE_ESC, KEYCODE_TAB } from '@aytm/lib/common/const'
// import exceptionReporter from '@aytm/lib/common/exception-reporter'
import ManagedRenderComponent from '@aytm/lib/common/components/ManagedRenderComponent'
import { sanitizeHTML } from '@aytm/lib/common/html'

const { hasCommandModifier } = KeyBindingUtil

const DEFAULT_DEBOUNCE = 400

export const defaultCustomStyleMap = {
  BOLD: { fontWeight: 'bold' },
  ITALIC: { fontStyle: 'italic' },
  UNDERLINE: { textDecoration: 'underline' }
}

export const defaultStyleMarkup = OrderedMap([
  ['BOLD', Map({ start: '<b>', end: '</b>' })],
  ['ITALIC', Map({ start: '<i>', end: '</i>' })],
  ['UNDERLINE', Map({ start: '<u>', end: '</u>' })]
  // ['CODE', Map({ start: '<code>', end: '</code>' })],
  // ['SUBSCRIPT', Map({ start: '<sub>', end: '</sub>' })],
  // ['SUPERSCRIPT', Map({ start: '<sup>', end: '</sup>' })]
])

export const defaultBlockTypes = OrderedMap({
  'header-one': Map({ start: '<h1>', end: '</h1>' }),
  'header-two': Map({ start: '<h2>', end: '</h2>' }),
  'header-three': Map({ start: '<h3>', end: '</h3>' }),
  blockquote: Map({ start: '<blockquote>', end: '</blockquote>' }),
  'code-block': Map({ start: '<pre><code>', end: '</code></pre>' }),
  'ordered-list-item': Map({ start: '<ol><li>', end: '</li></ol>' }),
  'unordered-list-item': Map({ start: '<ul><li>', end: '</li></ul>' })
})

export const defaultFormatOptions = Map({
  BOLD: true,
  ITALIC: true,
  UNDERLINE: true,
  blockquote: false,
  'header-one': false,
  'header-two': false,
  'header-three': false
})

export const defaultBlockMarkup = Map({ start: '', end: '', join: '<br>' })

// eslint-disable-next-line react/display-name
export const renderToolbarButtons = formatOptions => externalProps => (
  <>
    {false !== formatOptions?.get('BOLD') ? <BoldButton {...externalProps} /> : null}
    {false !== formatOptions?.get('ITALIC') ? <ItalicButton {...externalProps} /> : null}
    {false !== formatOptions?.get('UNDERLINE') ? <UnderlineButton {...externalProps} /> : null}
    {formatOptions?.get('blockquote') ? <BlockquoteButton {...externalProps} /> : null}
    {formatOptions?.get('header-one') ? <HeadlineOneButton {...externalProps} /> : null}
    {formatOptions?.get('header-two') ? <HeadlineTwoButton {...externalProps} /> : null}
    {formatOptions?.get('header-three') ? <HeadlineThreeButton {...externalProps} /> : null}
  </>
)

export const inlineToolbarPluginConfig = {
  theme: {
    buttonStyles: {
      buttonWrapper: 'rfd-ui-rich-text__buttonWrapper',
      button: 'rfd-ui-rich-text__button',
      active: 'rfd-ui-rich-text__active'
    },
    toolbarStyles: {
      toolbar: 'rfd-ui-rich-text__toolbar'
    }
  }
}

export const toolbarPluginConfig = {
  theme: {
    buttonStyles: {
      buttonWrapper: 'rfd-ui-rich-text__static__buttonWrapper',
      button: 'rfd-ui-rich-text__static__button',
      active: 'rfd-ui-rich-text__static__active'
    },
    toolbarStyles: {
      toolbar: 'rfd-ui-rich-text__static__toolbar'
    }
  }
}

import '@draft-js-plugins/inline-toolbar/lib/plugin.css'
import 'draft-js/dist/Draft.css'
import '@aytm/lib/rfd-ui/styles/advanced/rich-text.scss'

const createRichTextPlugin = ({ multiline, handleReturnOnSingleLine, handleReturn }) => {
  return {
    handleReturn: (event, editorState) => {
      if (!multiline) {
        if (-1 < getActiveRichTextContentElementIndex()) {
          if ('function' == typeof handleReturnOnSingleLine) {
            return handleReturnOnSingleLine(event, editorState, getNextRichTextContentElement()) || 'handled'
          }
          if ('function' == typeof handleReturn) {
            return handleReturn(event, editorState) || 'handled'
          } else {
            const nextRichTextContent = getNextRichTextContentElement()
            if (nextRichTextContent) {
              nextRichTextContent.focus()
              return 'handled'
            }
          }
        }
      } else {
        if ('function' == typeof handleReturn) {
          return handleReturn(event, editorState) || 'handled'
        }
      }
      return 'not-handled'
    }
  }
}

export default class RichText extends ManagedRenderComponent {
  static propTypes = {
    decorators: PropTypes.array,
    html: PropTypes.string,
    text: PropTypes.string,
    alignment: PropTypes.string,
    plugins: PropTypes.array,
    multiline: PropTypes.bool,
    multilineLimit: PropTypes.number,
    minHeight: PropTypes.number,
    className: PropTypes.string,
    debounce: PropTypes.number,
    customStyleMap: PropTypes.object,
    styleMarkup: PropTypes.instanceOf(OrderedMap),
    blockMarkup: PropTypes.instanceOf(Map),
    editorFormatting: PropTypes.bool,
    formatOptions: PropTypes.instanceOf(Map),
    disableFormatting: PropTypes.bool,
    disableDefaultPlugins: PropTypes.bool,
    readOnly: PropTypes.bool,
    editorState: PropTypes.instanceOf(EditorState),
    keyBindingFn: PropTypes.func,
    handleKeyCommand: PropTypes.func,
    handleReturn: PropTypes.func,
    handleReturnOnSingleLine: PropTypes.func,
    handlePastedText: PropTypes.func,
    handlePastedMultilineText: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    allowUpdate: PropTypes.func,
    allowUndo: PropTypes.bool,
    onUndo: PropTypes.func,
    onRedo: PropTypes.func
  }
  static updateOnProps = 'pure data'

  constructor(props) {
    super(props)
    this.setEditorState = this.setEditorState.bind(this);
    this.onEditorFocus = this.onEditorFocus.bind(this);
    this.onEditorBlur = this.onEditorBlur.bind(this);
    this.onEditorChange = this.onEditorChange.bind(this);
    this.editorKeyBinding = this.editorKeyBinding.bind(this);
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.handleReturn = this.handleReturn.bind(this);
    this.getTextLines = this.getTextLines.bind(this);
    this.getHTMLLines = this.getHTMLLines.bind(this);
    this.sanitizeHTML = this.sanitizeHTML.bind(this);
    this.handlePastedText = this.handlePastedText.bind(this);
    this.updateHTML = this.updateHTML.bind(this);
    this.updateText = this.updateText.bind(this);
    this.updatePlugins = this.updatePlugins.bind(this);
    this.updateDefaultPlugins = this.updateDefaultPlugins.bind(this);
    this.updateConverter = this.updateConverter.bind(this);
    this.getContent = this.getContent.bind(this);
    this.getHTML = this.getHTML.bind(this);
    this.getText = this.getText.bind(this);
    this.getEditorState = this.getEditorState.bind(this);
    this.forceUpdateEditorState = this.forceUpdateEditorState.bind(this);
    this.selectAll = this.selectAll.bind(this);
    this.setFocus = this.setFocus.bind(this);
    const { html, multiline, allowUndo = false } = props
    this.onChangeDebounceTimer = null
    this.lastHTML = ''
    this.InlineToolbar = null
    this.Toolbar = null
    this.focused = false
    this.updateDefaultPlugins(props)
    this.updatePlugins(props)
    this.updateConverter(props)
    const editorState = this.updateHTML({ html, multiline, setState: false })
    this.state = {
      editorState: allowUndo == editorState.getAllowUndo() ? editorState : EditorState.set(editorState, { allowUndo })
    }
  }

  setEditorState(editorState = EditorState.createEmpty(), callback) {
    const { allowUndo = false } = this.props
    this.setState(
      {
        editorState: allowUndo == editorState.getAllowUndo() ? editorState : EditorState.set(editorState, { allowUndo })
      },
      callback
    )
  }

  onEditorFocus(event) {
    this.focused = true
    const { onFocus } = this.props
    if ('function' === typeof onFocus) {
      onFocus(event, this.state.editorState)
    }
    return 'not-handled'
  }

  onEditorBlur(event) {
    this.focused = false
    const { onChange, onBlur } = this.props
    if ('function' === typeof onChange && this.onChangeDebounceTimer != null) {
      clearTimeout(this.onChangeDebounceTimer)
      onChange(this.getContent, this.state.editorState)
      this.onChangeDebounceTimer = null
    }
    if ('function' === typeof onBlur) {
      onBlur(event, this.state.editorState)
    }
    return 'not-handled'
  }

  onEditorChange(changedEditorState, { applyPlugins = false } = {}) {
    // apply plugins to changed state if requested
    const editorState = applyPlugins
      ? (this.props.plugins || []).reduce(
          (editorState, plugin) => (plugin && plugin.onChange ? plugin.onChange(editorState) : editorState),
          changedEditorState
        )
      : changedEditorState

    const { readOnly, onChange, onUpdate, allowUpdate, debounce = DEFAULT_DEBOUNCE } = this.props
    const prevEditorState = this.state.editorState

    if (readOnly) {
      return
    }
    if ('function' === typeof allowUpdate && !allowUpdate(prevEditorState, editorState)) {
      //TODO: force update editorState
      this.setState({ editorState: EditorState.forceSelection(prevEditorState, prevEditorState.getSelection()) })
    } else {
      this.setEditorState(editorState, () => {
        if ('function' === typeof onUpdate) {
          onUpdate(editorState, prevEditorState, this.getContent)
        }
        if ('function' === typeof onChange && prevEditorState.getCurrentContent() != editorState.getCurrentContent()) {
          if (null != this.onChangeDebounceTimer) {
            clearTimeout(this.onChangeDebounceTimer)
          }
          const onChangeDebounceTimer = (this.onChangeDebounceTimer = setTimeout(() => {
            onChange(this.getContent, this.state.editorState)
            if (onChangeDebounceTimer == this.onChangeDebounceTimer) {
              this.onChangeDebounceTimer = null
            }
          }, debounce))
        }
      })
    }
  }

  editorKeyBinding(event) {
    switch (event.which) {
      case KEYCODE_ESC: {
        // onEscape
        if (this.props.onEscape) {
          this.props.onEscape(event)
        }
        break
      }
      case KEYCODE_TAB: {
        // onTab
        if (this.props.onTab) {
          this.props.onTab(event)
        }
        break
      }
      case KEYCODE_UP: {
        // onUpArrow
        if (this.props.onUpArrow) {
          this.props.onUpArrow(event)
        }
        break
      }
      case KEYCODE_DOWN: {
        // onDownArrow
        if (this.props.onDownArrow) {
          this.props.onDownArrow(event)
        }
        break
      }
    }
    const defaultKeyBinding = getDefaultKeyBinding(event)
    if ('function' === typeof this.props.keyBindingFn) {
      const customKeyBindingFnResult = this.props.keyBindingFn(event)
      if (customKeyBindingFnResult && defaultKeyBinding !== customKeyBindingFnResult) {
        return customKeyBindingFnResult
      }
    }
    if (hasCommandModifier(event)) {
      switch (event.keyCode) {
        case 66 /* ctrl+b */: {
          return 'toggle-inline-style-bold'
        }
        case 73 /* ctrl+i */: {
          return 'toggle-inline-style-italic'
        }
        case 85 /* ctrl+u */: {
          return 'toggle-inline-style-underline'
        }
      }
    }
    switch (defaultKeyBinding) {
      case 'undo': {
        return this.props.allowUndo ? 'undo' : 'vundo' // pass to virtual undo if allowUndo is false
      }
      case 'redo': {
        return this.props.allowUndo ? 'redo' : 'vredo' // pass to virtual redo if allowUndo is false
      }
      default: {
        return null == defaultKeyBinding ? void 0 : defaultKeyBinding
      }
    }
  }

  handleKeyCommand(command, ...handleKeyCommandRestArgs) {
    const { readOnly, editorFormatting, disableFormatting, handleKeyCommand: customHandleKeyCommand } = this.props
    if (readOnly) {
      return 'handled'
    }
    if (customHandleKeyCommand && 'handled' === customHandleKeyCommand(command, ...handleKeyCommandRestArgs)) {
      return 'handled'
    }
    const { editorState } = this.state
    switch (command) {
      case 'toggle-inline-style-bold': {
        if (!disableFormatting || editorFormatting)
          this.onEditorChange(RichUtils.toggleInlineStyle(editorState, 'BOLD'), { applyPlugins: true })
        return 'handled'
      }
      case 'toggle-inline-style-italic': {
        if (!disableFormatting || editorFormatting)
          this.onEditorChange(RichUtils.toggleInlineStyle(editorState, 'ITALIC'), { applyPlugins: true })
        return 'handled'
      }
      case 'toggle-inline-style-underline': {
        if (!disableFormatting || editorFormatting)
          this.onEditorChange(RichUtils.toggleInlineStyle(editorState, 'UNDERLINE'), { applyPlugins: true })
        return 'handled'
      }
      case 'vundo': {
        const { onUndo = window.rfdUIRichTextUndo } = this.props
        if ('function' == typeof onUndo) {
          const { editorState } = this.state
          const { onChange } = this.props
          if ('function' == typeof onChange && this.onChangeDebounceTimer != null) {
            clearTimeout(this.onChangeDebounceTimer)
            onChange(this.getContent, editorState)
            this.onChangeDebounceTimer = null
            setTimeout(() => {
              onUndo(editorState, (editorState, callback) => {
                this.setEditorState(editorState, callback)
              })
            }, 0)
          } else {
            onUndo(editorState, (editorState, callback) => {
              this.setEditorState(editorState, callback)
            })
          }
        }
        return 'handled'
      }
      case 'vredo': {
        const { onRedo = window.rfdUIRichTextRedo } = this.props
        if ('function' == typeof onRedo) {
          const { editorState } = this.state
          const { onChange } = this.props
          if ('function' == typeof onChange && this.onChangeDebounceTimer != null) {
            clearTimeout(this.onChangeDebounceTimer)
            onChange(this.getContent, editorState)
            this.onChangeDebounceTimer = null
            setTimeout(() => {
              onRedo(editorState, (editorState, callback) => {
                this.setEditorState(editorState, callback)
              })
            }, 0)
          } else {
            onRedo(editorState, (editorState, callback) => {
              this.setEditorState(editorState, callback)
            })
          }
        }
        return 'handled'
      }
      default: {
        return 'not-handled'
      }
    }
  }

  handleReturn(/*event, editorState*/) {
    const { readOnly } = this.props
    if (readOnly) {
      return 'handled'
    }
    return 'not-handled'
  }

  getTextLines(text = '') {
    return text && text.length ? text.split('\n') : []
  }

  getHTMLLines(html = '', textLines = [], editorState) {
    return html && html.length
      ? (() => {
          const { contentBlocks, entityMap } = convertFromHTML(html)
          const htmlEditorState = contentBlocks
            ? EditorState.createWithContent(
                ContentState.createFromBlockArray(contentBlocks, entityMap),
                editorState.getDecorator()
              )
            : EditorState.createEmpty()
          return this.convertBlockMapToHTML(htmlEditorState.getCurrentContent().getBlockMap())
            .split(/<br\s*\/?>/)
            .map(line => line.trim())
            .reduce(
              function ({ textIndex, lines }, line) {
                return 0 == line.length && (null == textLines[textIndex] || 0 != textLines[textIndex].length)
                  ? { textIndex, lines }
                  : {
                      textIndex: textIndex + 1,
                      lines: (lines.push(line), lines)
                    }
              },
              { textIndex: 0, lines: [] }
            ).lines
        })()
      : []
  }

  sanitizeHTML(html = '') {
    const { formatOptions = defaultFormatOptions } = this.props

    const allowedTags = []

    if (false !== formatOptions.get('BOLD')) {
      allowedTags.push('b')
    }
    if (false !== formatOptions.get('ITALIC')) {
      allowedTags.push('i')
    }
    if (false !== formatOptions.get('UNDERLINE')) {
      allowedTags.push('u')
    }
    if (true === formatOptions.get('blockquote')) {
      allowedTags.push('blockquote')
    }
    if (true === formatOptions.get('header-one')) {
      allowedTags.push('h1')
    }
    if (true === formatOptions.get('header-two')) {
      allowedTags.push('h2')
    }
    if (true === formatOptions.get('header-three')) {
      allowedTags.push('h3')
    }

    const sanitizedHTML = sanitizeHTML(html, { allowedTags, includeDefault: true })

    return sanitizedHTML
  }

  handlePastedText(text = '', _html = '', editorState) {
    let html = this.sanitizeHTML(_html)
    const { readOnly, disableFormatting, multiline, handlePastedMultilineText, handlePastedText } = this.props
    if (readOnly) {
      return 'handled'
    }

    if (disableFormatting) {
      html = null
    }
    const textLines = this.getTextLines(text)
    let htmlLines = this.getHTMLLines(html, textLines, editorState)
    if (null == text || 0 == text.length) {
      return 'handled'
    }
    if (textLines.length > 1 && htmlLines.length < textLines.length) {
      //TODO quick fix for IE
      htmlLines = textLines.map(line => line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'))
    }
    if ((textLines.length > 1 || htmlLines.length > 1) && 'function' == typeof handlePastedMultilineText) {
      const pasteResult =
        handlePastedMultilineText(textLines, htmlLines, editorState, editorState => {
          this.onEditorChange(editorState, { applyPlugins: true })
        }) || 'handled'
      if ('handled' == pasteResult) {
        return 'handled'
      }
    }
    if ('function' == typeof handlePastedText) {
      const pasteResult =
        handlePastedText(textLines.join('\n'), htmlLines.join('<br />'), editorState, editorState => {
          this.onEditorChange(editorState, { applyPlugins: true })
        }) || 'handled'
      if ('handled' == pasteResult) {
        return 'handled'
      }
    }
    if (htmlLines.length) {
      const { contentBlocks, entityMap } = convertFromHTML(multiline ? htmlLines.join('<br />') : htmlLines.join(' '))
      const htmlBlockMap = ContentState.createFromBlockArray(contentBlocks, entityMap).getBlockMap()
      this.onEditorChange(
        EditorState.push(
          editorState,
          Modifier.replaceWithFragment(editorState.getCurrentContent(), editorState.getSelection(), htmlBlockMap),
          'insert-fragment'
        ),
        { applyPlugins: true }
      )
    } else if (textLines.length) {
      const textBlockMap = ContentState.createFromText(
        multiline ? textLines.join('\n') : textLines.join(' ')
      ).getBlockMap()
      this.onEditorChange(
        EditorState.push(
          editorState,
          Modifier.replaceWithFragment(editorState.getCurrentContent(), editorState.getSelection(), textBlockMap),
          'insert-fragment'
        ),
        { applyPlugins: true }
      )
    }

    return 'handled'
  }

  updateHTML({ html = '', multiline = true, setState = true }) {
    const { onUpdate } = this.props
    const { editorState: prevEditorState = EditorState.createEmpty() } = this.state || {}

    const editorState = listPipe(
      [
        () => {
          if (null != html && html.length) {
            const { contentBlocks, entityMap } = convertFromHTML(html.replace(/(\s+)$/, '<span>$1</span>'))

            if (!contentBlocks || 0 === contentBlocks.length) {
              return EditorState.createEmpty()
            }

            return (multiline ? x => x : x => this.singleLinePlugin.onChange(x))(
              EditorState.createWithContent(
                ContentState.createFromBlockArray(contentBlocks, entityMap),
                prevEditorState.getDecorator()
              )
            )
          } else {
            return EditorState.createEmpty()
          }
        },
        editorState => {
          if (setState && prevEditorState.getSelection().getHasFocus()) {
            return EditorState.forceSelection(editorState, setSelectionAfterUpdate(editorState, prevEditorState))
          }
          return null
        }
      ],
      null,
      true
    )

    if (setState) {
      this.setEditorState(editorState)
    }

    if ('function' == typeof onUpdate) {
      onUpdate(editorState, prevEditorState, this.getContent)
    }

    this.lastHTML = html

    return editorState
  }

  updateText({ text = '', multiline = true, setState = true }) {
    return this.updateHTML({ html: escapeHTML(text), multiline, setState })
  }

  updatePlugins(props = this.props) {
    const { plugins } = props
    this.plugins = null == plugins ? this.defaultPlugins : [...plugins, ...this.defaultPlugins]

    return this.plugins
  }

  updateDefaultPlugins(props = this.props) {
    const { disableDefaultPlugins, disableFormatting, editorFormatting, multilineLimit, multiline = true } = props
    this.defaultPlugins = disableDefaultPlugins
      ? []
      : [
          defaultPlugins => [...defaultPlugins, createArrowNavigationPlugin({ getEditorState: this.getEditorState })],
          defaultPlugins => [...defaultPlugins, createRichTextPlugin(this.props)],
          defaultPlugins => {
            if (editorFormatting) {
              const toolbarPlugin = createToolbarPlugin(toolbarPluginConfig)
              this.Toolbar = toolbarPlugin.Toolbar
              return [...defaultPlugins, toolbarPlugin]
            } else {
              this.Toolbar = null
              return defaultPlugins
            }
          },
          defaultPlugins => {
            if (disableFormatting) {
              this.InlineToolbar = null
              return defaultPlugins
            } else {
              const inlineToolbarPlugin = createInlineToolbarPlugin(inlineToolbarPluginConfig)
              this.InlineToolbar = inlineToolbarPlugin.InlineToolbar
              return [...defaultPlugins, inlineToolbarPlugin]
            }
          },
          defaultPlugins => {
            if (multiline) {
              this.singleLinePlugin = null
              return defaultPlugins
            } else {
              this.singleLinePlugin = createSingleLinePlugin()
              return [...defaultPlugins, this.singleLinePlugin]
            }
          },
          defaultPlugins => {
            if (multilineLimit) {
              this.multilineLimitPlugin = createMultilineLimitPlugin({ maxLines: multilineLimit })
              return [...defaultPlugins, this.multilineLimitPlugin]
            } else {
              this.multilineLimitPlugin = null
              return defaultPlugins
            }
          }
        ].reduce((defaultPlugins, f) => f(defaultPlugins), [])
  }

  updateConverter(props = this.props) {
    const { styleMarkup = defaultStyleMarkup, blockTypes = defaultBlockTypes, blockMarkup = defaultBlockMarkup } = props
    this.convertBlockMapToHTML = convertBlockMapToHTML({
      styleMarkup,
      blockMarkup,
      blockTypes
    })
  }

  getContent({ editorState = this.state.editorState, text: fetchText = true, html: fetchHTML = true } = {}) {
    const content = editorState.getCurrentContent()
    const blockMap = content.getBlockMap()
    const result = Object.assign(
      fetchText ? { text: blockMap.map(b => b.get('text')).join('\n') } : {},
      fetchHTML ? { html: this.convertBlockMapToHTML(blockMap) } : {}
    )
    if (fetchHTML && editorState == this.state.editorState) {
      this.lastHTML = result.html || ''
    }
    return result
  }

  getHTML({ editorState = this.state.editorState } = {}) {
    return this.getContent({
      editorState,
      text: false,
      html: true
    }).html
  }

  getText({ editorState = this.state.editorState } = {}) {
    return this.getContent({
      editorState,
      text: true,
      html: false
    }).text
  }

  getEditorState() {
    return this.state.editorState
  }

  componentDidMount() {
    const { html = '' } = this.props
    if (html != this.lastHTML) {
      this.updateHTML({ html })
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const nextHTML = nextProps.html || ''
    const prevHTML = this.props.html || ''
    if (nextHTML != prevHTML && nextHTML != this.lastHTML) {
      this.updateHTML({ html: nextHTML })
    } else if (this.props.text != nextProps.text) {
      this.updateText({ text: nextProps.text })
    }
    if (nextProps.plugins != this.props.plugins) {
      this.updatePlugins(nextProps)
    }
    if (nextProps.multiline != this.props.multiline) {
      this.updateDefaultPlugins(nextProps)
    }
    if (nextProps.styleMarkup != this.props.styleMarkup || nextProps.blockMarkup != this.props.blockMarkup) {
      this.updateConverter(nextProps)
    }
  }

  forceUpdateEditorState() {
    const editorState = this.state.editorState
    const selection = editorState.getSelection()
    const newEditorState = EditorState.createWithContent(editorState.getCurrentContent(), editorState.getDecorator())
    this.setEditorState(
      selection.get('hasFocus') ? EditorState.forceSelection(newEditorState, selection) : newEditorState
    )
  }

  selectAll(force = true) {
    this.setState(state => {
      const { editorState } = state
      const currentContent = editorState.getCurrentContent()
      const firstBlock = currentContent.getBlockMap().first()
      const lastBlock = currentContent.getBlockMap().last()
      const firstBlockKey = firstBlock.getKey()
      const lastBlockKey = lastBlock.getKey()
      const selection = editorState.getSelection().merge({
        anchorKey: firstBlockKey,
        anchorOffset: 0,
        focusKey: lastBlockKey,
        focusOffset: lastBlock.getLength()
      })
      return {
        ...state,
        editorState: force
          ? EditorState.forceSelection(editorState, selection)
          : EditorState.acceptSelection(editorState, selection)
      }
    })
  }

  setFocus(_event) {
    if (!this.focused && 'function' === typeof this.refEditor?.focus) {
      this.refEditor.focus()
    }
  }

  render() {
    const {
      readOnly = false,
      // eslint-disable-next-line no-unused-vars
      multiline = true,
      html,
      className,
      minHeight,
      alignment = 'left',
      decorators = [],
      onChange,
      allowUpdate,
      onFocus,
      onBlur,
      onEscape,
      onTab,
      onUpArrow,
      onDownArrow,
      debounce,
      plugins: externalPlugins,
      editorState: externalEditorState,
      keyBindingFn,
      handleKeyCommand,
      handleReturn,
      handleReturnOnSingleLine,
      handlePastedText,
      handlePastedMultilineText,
      customStyleMap,
      styleMarkup,
      blockMarkup,
      editorFormatting,
      formatOptions,
      disableFormatting,
      disableDefaultPlugins,
      spellCheck,
      allowUndo,
      onUndo,
      onRedo,
      ...otherEditorProps
    } = this.props
    const { editorState } = this.state
    return (
      <div
        onClick={minHeight && !this.focused ? this.setFocus : null}
        className={`rfd-ui-rich-text${className ? ` ${className}` : ''}`}
        style={minHeight ? { minHeight } : null}
      >
        <Editor
          readOnly={readOnly}
          textAlignment={alignment}
          plugins={this.plugins}
          decorators={decorators}
          editorState={externalEditorState || editorState}
          onChange={this.onEditorChange}
          onFocus={this.onEditorFocus}
          onBlur={this.onEditorBlur}
          keyBindingFn={this.editorKeyBinding}
          handleKeyCommand={this.handleKeyCommand}
          handleReturn={this.handleReturn}
          handlePastedText={this.handlePastedText}
          customStyleMap={customStyleMap || (disableFormatting ? null : defaultCustomStyleMap)}
          spellCheck={void 0 == spellCheck ? true : spellCheck}
          ref={this.saveRef('Editor')}
          {...otherEditorProps}
        />
        {this.InlineToolbar && <this.InlineToolbar>{renderToolbarButtons(formatOptions)}</this.InlineToolbar>}
        {this.Toolbar && <this.Toolbar>{renderToolbarButtons(formatOptions)}</this.Toolbar>}
      </div>
    )
  }

  componentDidCatch(_error, _info) {
    // exceptionReporter(error, info) // skip error reporting on Draft.js
    const { html, multiline, allowUndo = false } = this.props
    this.onChangeDebounceTimer = null
    this.lastHTML = ''
    this.InlineToolbar = null
    this.Toolbar = null
    this.updateDefaultPlugins(this.props)
    this.updatePlugins(this.props)
    this.updateConverter(this.props)
    const editorState = this.updateHTML({ html, multiline, setState: false })
    this.setState({
      editorState: allowUndo == editorState.getAllowUndo() ? editorState : EditorState.set(editorState, { allowUndo })
    })
  }
}
